import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControl, InputLabel, Select, Divider, TablePagination, Snackbar, Alert } from '@mui/material';
import { getAllCoupons, createCoupon } from '../services/userService';
import { buildCrmApiUrl } from '../api/apiConfig';
import { useNavigate } from 'react-router-dom';
import { Business as BusinessIcon, Dashboard as DashboardIcon } from '@mui/icons-material';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function DashboardPage() {
  const [tabValue, setTabValue] = useState(0);
  const [coupons, setCoupons] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    description: '',
    type: 'percentage',
    value: '',
    min_purchase_amount: '',
    max_discount_amount: '',
    start_date: '',
    expiry_date: '',
    usage_limit: '',
    per_user_limit: '',
    applicability: 'all_plans',
    applicable_plan_names: [],
    applicable_features: [],
    is_number_specific: false,
    applicable_numbers: [],
    open: false,
    algo: false,
    frequency: 'one-time',
    owner: ''
  });

  // Owners for the coupon Owner dropdown: a payment applying the coupon is credited to
  // this owner. Fetched from the CRM owner-access list. Also loads the caller's role and
  // the owner coupon caps (owners cannot exceed the cap; admins are unlimited).
  const [ownersList, setOwnersList] = useState([]);
  const [myRole, setMyRole] = useState('');
  const [myOwner, setMyOwner] = useState('');
  const [couponLimits, setCouponLimits] = useState([]);
  const crmHeaders = { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' };
  useEffect(() => {
    const num = localStorage.getItem('number');
    if (!num) return;
    fetch(buildCrmApiUrl(`owner-access?number=${encodeURIComponent(num)}`), { headers: crmHeaders })
      .then((r) => r.json())
      .then((d) => { if (d?.success && Array.isArray(d.data)) setOwnersList(d.data); })
      .catch(() => {});
    fetch(buildCrmApiUrl(`owner-access/me?number=${encodeURIComponent(num)}`), { headers: crmHeaders })
      .then((r) => r.json())
      .then((d) => {
        const r = d?.data?.role || '';
        // Coupons are for sales people and the admin: anyone else (a support user, a
        // number with no sales access) is sent back. obalgo enforces the same rule.
        if (d?.success && !['admin', 'leader', 'executive'].includes(r)) { navigate('/sales'); return; }
        setMyRole(r === 'leader' || r === 'executive' ? 'owner' : r); setMyOwner(d?.data?.owner || '');
      })
      .catch(() => {});
    fetch(buildCrmApiUrl(`coupon-limits?number=${encodeURIComponent(num)}`), { headers: crmHeaders })
      .then((r) => r.json())
      .then((d) => { if (d?.success && Array.isArray(d.data)) setCouponLimits(d.data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // couponCapError returns an error string if an owner's coupon exceeds the cap, else "".
  // Admins are unlimited. The cap is the per-plan limit for a specific-plans coupon (most
  // restrictive across the selected plans), otherwise the all-plans default (plan_id "").
  const couponCapError = () => {
    if (myRole !== 'owner') return '';
    const val = Number(newCoupon.value);
    if (isNaN(val)) return '';
    const byPlan = {};
    couponLimits.forEach((l) => { byPlan[l.plan_id || ''] = l; });
    const defaultLimit = byPlan[''];
    let applicable = [];
    if (newCoupon.applicability === 'specific_plans' && newCoupon.applicable_plan_names.length > 0) {
      applicable = newCoupon.applicable_plan_names.map((p) => byPlan[p] || defaultLimit).filter(Boolean);
    } else if (defaultLimit) {
      applicable = [defaultLimit];
    }
    if (applicable.length === 0) return ''; // no cap defined
    // Algo and non-algo coupons have separate caps.
    const pctField = newCoupon.algo ? 'algo_max_percentage' : 'max_percentage';
    const fixedField = newCoupon.algo ? 'algo_max_fixed_amount' : 'max_fixed_amount';
    if (newCoupon.type === 'percentage') {
      const caps = applicable.map((l) => l[pctField]).filter((m) => m > 0);
      if (caps.length && val > Math.min(...caps)) return `Max allowed for you is ${Math.min(...caps)}%`;
    } else {
      const caps = applicable.map((l) => l[fixedField]).filter((m) => m > 0);
      if (caps.length && val > Math.min(...caps)) return `Max allowed for you is ₹${Math.min(...caps)}`;
    }
    return '';
  };

  // Admin-only management of owner coupon caps.
  const [openLimitsDialog, setOpenLimitsDialog] = useState(false);
  const [newLimit, setNewLimit] = useState({ plan_id: '', plan_name: '', max_percentage: '', max_fixed_amount: '', algo_max_percentage: '', algo_max_fixed_amount: '' });
  const refetchLimits = () => {
    const num = localStorage.getItem('number');
    fetch(buildCrmApiUrl(`coupon-limits?number=${encodeURIComponent(num)}`), { headers: crmHeaders })
      .then((r) => r.json()).then((d) => { if (d?.success && Array.isArray(d.data)) setCouponLimits(d.data); }).catch(() => {});
  };
  const handleUpsertLimit = async () => {
    const num = localStorage.getItem('number');
    try {
      const res = await fetch(buildCrmApiUrl(`coupon-limits?number=${encodeURIComponent(num)}`), {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...crmHeaders },
        body: JSON.stringify({
          plan_id: newLimit.plan_id.trim(), plan_name: newLimit.plan_name.trim(),
          max_percentage: Number(newLimit.max_percentage) || 0, max_fixed_amount: Number(newLimit.max_fixed_amount) || 0,
          algo_max_percentage: Number(newLimit.algo_max_percentage) || 0, algo_max_fixed_amount: Number(newLimit.algo_max_fixed_amount) || 0
        })
      });
      const d = await res.json();
      if (d.success) { setSnackbar({ open: true, message: 'Limit saved', severity: 'success' }); setNewLimit({ plan_id: '', plan_name: '', max_percentage: '', max_fixed_amount: '', algo_max_percentage: '', algo_max_fixed_amount: '' }); refetchLimits(); }
      else setSnackbar({ open: true, message: d.error || 'Failed to save limit', severity: 'error' });
    } catch (e) { setSnackbar({ open: true, message: e.message, severity: 'error' }); }
  };
  const handleDeleteLimit = async (id) => {
    const num = localStorage.getItem('number');
    try {
      const res = await fetch(buildCrmApiUrl(`coupon-limits/${id}?number=${encodeURIComponent(num)}`), { method: 'DELETE', headers: crmHeaders });
      const d = await res.json();
      if (d.success) refetchLimits();
      else setSnackbar({ open: true, message: d.error || 'Failed to delete', severity: 'error' });
    } catch (e) { setSnackbar({ open: true, message: e.message, severity: 'error' }); }
  };

  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    fetchCoupons();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    // const kycStatus = localStorage.getItem('kyc_status');

    const loginTime = localStorage.getItem('loginTime');
    const loginDate = new Date(loginTime);
    const currentDate = new Date();
    const diffInMilliseconds = currentDate - loginDate;

    // Step 5: Convert milliseconds to minutes
    const diffInMinutes = Math.floor(diffInMilliseconds / 60000);


    if (!!token && diffInMinutes < 60*24*20) {
    //   navigate('/admin');
    }else{
      navigate('/');
    }
  }, [navigate]);

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllCoupons(token, {});
      if (response.success === 1) {
        setCoupons(response.data);
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newCoupon.code.trim()) newErrors.code = 'Code is required';
    if (!newCoupon.description.trim()) newErrors.description = 'Description is required';
    if (!newCoupon.value) newErrors.value = 'Value is required';
    if (!newCoupon.min_purchase_amount) newErrors.min_purchase_amount = 'Min Purchase Amount is required';
    if (!newCoupon.max_discount_amount) newErrors.max_discount_amount = 'Max Discount Amount is required';
    if (!newCoupon.start_date) newErrors.start_date = 'Start Date is required';
    if (!newCoupon.expiry_date) newErrors.expiry_date = 'Expiry Date is required';
    if (!newCoupon.usage_limit) newErrors.usage_limit = 'Usage Limit is required';
    if (!newCoupon.per_user_limit) newErrors.per_user_limit = 'Per User Limit is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateCoupon = async () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    // Owners cannot exceed their coupon cap (admins are unlimited).
    const capError = couponCapError();
    if (capError) {
      setSnackbar({ open: true, message: capError, severity: 'error' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Convert string number fields to actual numbers
      const couponData = {
        ...newCoupon,
        // An owner's coupon is always credited to themselves; admins use their selection.
        owner: myRole === 'owner' ? myOwner : newCoupon.owner,
        value: Number(newCoupon.value),
        min_purchase_amount: Number(newCoupon.min_purchase_amount),
        max_discount_amount: Number(newCoupon.max_discount_amount),
        usage_limit: Number(newCoupon.usage_limit),
        per_user_limit: Number(newCoupon.per_user_limit)
      };

      const response = await createCoupon(token, couponData);
      if (response.success === 1) {
        setOpenDialog(false);
        fetchCoupons();
        setNewCoupon({
          code: '',
          description: '',
          type: 'percentage',
          value: '',
          min_purchase_amount: '',
          max_discount_amount: '',
          start_date: '',
          expiry_date: '',
          usage_limit: '',
          per_user_limit: '',
          applicability: 'all_plans',
          applicable_plan_names: [],
          applicable_features: [],
          is_number_specific: false,
          applicable_numbers: [],
          open: false,
          algo: false,
          frequency: 'one-time',
          owner: ''
        });
        setErrors({});
        setSnackbar({
          open: true,
          message: 'Coupon created successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Error creating coupon',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error creating coupon:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error creating coupon';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 5));
    setPage(0);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Parent Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={1} onChange={(e, newValue) => {
          if (newValue === 0) navigate('/sales');
        }}>
          <Tab 
            label="Sales" 
            icon={<BusinessIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Admin Dashboard" 
            icon={<DashboardIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Admin Dashboard Sub-tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Coupons" />
          <Tab label="Users" disabled />
          <Tab label="Settings" disabled />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>Current Coupons</Typography>
        
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell sx={{ minWidth: '300px' }}>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Min Purchase</TableCell>
                <TableCell>Max Discount</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Usage Limit</TableCell>
                <TableCell>Per User Limit</TableCell>
                <TableCell>Applicability</TableCell>
                <TableCell>Applicable Plans</TableCell>
                <TableCell>Number Specific</TableCell>
                <TableCell>Applicable Numbers</TableCell>
                <TableCell>Open</TableCell>
                <TableCell>Algo</TableCell>
                <TableCell>Frequency</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((coupon) => (
                  <TableRow key={coupon.ID}>
                    <TableCell>{coupon.Code}</TableCell>
                    <TableCell sx={{ minWidth: '300px' }}>{coupon.Description}</TableCell>
                    <TableCell>{coupon.Type}</TableCell>
                    <TableCell>{coupon.Value}</TableCell>
                    <TableCell>{coupon.MinPurchaseAmount}</TableCell>
                    <TableCell>{coupon.MaxDiscountAmount}</TableCell>
                    <TableCell>{new Date(coupon.StartDate).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(coupon.ExpiryDate).toLocaleDateString()}</TableCell>
                    <TableCell>{coupon.Status}</TableCell>
                    <TableCell>{coupon.UsageLimit}</TableCell>
                    <TableCell>{coupon.PerUserLimit}</TableCell>
                    <TableCell>{coupon.Applicability}</TableCell>
                    <TableCell>{coupon.ApplicablePlanIDs?.join(', ') || '-'}</TableCell>
                    <TableCell>{coupon.IsNumberSpecific ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{coupon.ApplicableNumbers?.join(', ') || '-'}</TableCell>
                    <TableCell>{coupon.Open ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{coupon.Algo ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{coupon.Frequency || '-'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={coupons.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ width: '100%', mb: 1 }}>Create New Coupon</Typography>
          <Button variant="contained" color="primary" onClick={() => setOpenDialog(true)}>
            Create New Coupon
          </Button>
          {myRole === 'admin' && (
            <Button variant="outlined" onClick={() => setOpenLimitsDialog(true)}>
              Manage Owner Coupon Limits
            </Button>
          )}
        </Box>

        {/* Admin: define the max discount an owner may put on a coupon. plan_id blank = all-plans default. */}
        <Dialog open={openLimitsDialog} onClose={() => setOpenLimitsDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Owner Coupon Limits</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Caps the discount an owner can put on a coupon (admins are unlimited). Leave Plan ID blank for the all-plans default; add a specific Plan ID to override it for that plan.
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Plan</TableCell>
                    <TableCell>Non-Algo Max %</TableCell>
                    <TableCell>Non-Algo Max ₹</TableCell>
                    <TableCell>Algo Max %</TableCell>
                    <TableCell>Algo Max ₹</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {couponLimits.length === 0 ? (
                    <TableRow><TableCell colSpan={6}>No limits defined.</TableCell></TableRow>
                  ) : couponLimits.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.plan_id ? (l.plan_name || l.plan_id) : 'All plans'}</TableCell>
                      <TableCell>{l.max_percentage || '—'}</TableCell>
                      <TableCell>{l.max_fixed_amount || '—'}</TableCell>
                      <TableCell>{l.algo_max_percentage || '—'}</TableCell>
                      <TableCell>{l.algo_max_fixed_amount || '—'}</TableCell>
                      <TableCell><Button size="small" color="error" onClick={() => handleDeleteLimit(l.id)}>Delete</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Add / Update a limit</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Plan ID (blank = all plans)" value={newLimit.plan_id} onChange={(e) => setNewLimit({ ...newLimit, plan_id: e.target.value })} />
              <TextField label="Plan Name (label)" value={newLimit.plan_name} onChange={(e) => setNewLimit({ ...newLimit, plan_name: e.target.value })} />
              <TextField label="Non-Algo Max Percentage %" type="number" value={newLimit.max_percentage} onChange={(e) => setNewLimit({ ...newLimit, max_percentage: e.target.value })} inputProps={{ min: 0, max: 100 }} />
              <TextField label="Non-Algo Max Fixed Amount ₹" type="number" value={newLimit.max_fixed_amount} onChange={(e) => setNewLimit({ ...newLimit, max_fixed_amount: e.target.value })} inputProps={{ min: 0 }} />
              <TextField label="Algo Max Percentage %" type="number" value={newLimit.algo_max_percentage} onChange={(e) => setNewLimit({ ...newLimit, algo_max_percentage: e.target.value })} inputProps={{ min: 0, max: 100 }} />
              <TextField label="Algo Max Fixed Amount ₹" type="number" value={newLimit.algo_max_fixed_amount} onChange={(e) => setNewLimit({ ...newLimit, algo_max_fixed_amount: e.target.value })} inputProps={{ min: 0 }} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenLimitsDialog(false)}>Close</Button>
            <Button variant="contained" onClick={handleUpsertLimit}>Save Limit</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create New Coupon</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
              <TextField
                label="Code"
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })}
                required
                error={!!errors.code}
                helperText={errors.code}
              />
              <TextField
                label="Description"
                value={newCoupon.description}
                onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                multiline
                rows={2}
                required
                error={!!errors.description}
                helperText={errors.description}
              />
              <FormControl>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newCoupon.type}
                  onChange={(e) => setNewCoupon({ ...newCoupon, type: e.target.value })}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Value"
                type="number"
                value={newCoupon.value}
                onChange={(e) => setNewCoupon({ ...newCoupon, value: e.target.value })}
                inputProps={{ min: 0 }}
                error={!!errors.value || isNaN(Number(newCoupon.value)) || !!couponCapError()}
                helperText={errors.value || couponCapError() || (isNaN(Number(newCoupon.value)) ? "Please enter a valid number" : "")}
                required
              />
              <TextField
                label="Min Purchase Amount"
                type="number"
                value={newCoupon.min_purchase_amount}
                onChange={(e) => setNewCoupon({ ...newCoupon, min_purchase_amount: e.target.value })}
                inputProps={{ min: 0 }}
                error={!!errors.min_purchase_amount || isNaN(Number(newCoupon.min_purchase_amount))}
                helperText={errors.min_purchase_amount || (isNaN(Number(newCoupon.min_purchase_amount)) ? "Please enter a valid number" : "")}
                required
              />
              <TextField
                label="Max Discount Amount"
                type="number"
                value={newCoupon.max_discount_amount}
                onChange={(e) => setNewCoupon({ ...newCoupon, max_discount_amount: e.target.value })}
                inputProps={{ min: 0 }}
                error={!!errors.max_discount_amount || isNaN(Number(newCoupon.max_discount_amount))}
                helperText={errors.max_discount_amount || (isNaN(Number(newCoupon.max_discount_amount)) ? "Please enter a valid number" : "")}
                required
              />
              <TextField
                label="Start Date"
                type="date"
                value={newCoupon.start_date}
                onChange={(e) => setNewCoupon({ ...newCoupon, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                error={!!errors.start_date}
                helperText={errors.start_date}
                required
              />
              <TextField
                label="Expiry Date"
                type="date"
                value={newCoupon.expiry_date}
                onChange={(e) => setNewCoupon({ ...newCoupon, expiry_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                error={!!errors.expiry_date}
                helperText={errors.expiry_date}
                required
              />
              <TextField
                label="Usage Limit"
                type="number"
                value={newCoupon.usage_limit}
                onChange={(e) => setNewCoupon({ ...newCoupon, usage_limit: e.target.value })}
                inputProps={{ min: 0 }}
                error={!!errors.usage_limit || isNaN(Number(newCoupon.usage_limit))}
                helperText={errors.usage_limit || (isNaN(Number(newCoupon.usage_limit)) ? "Please enter a valid number" : "")}
                required
              />
              <TextField
                label="Per User Limit"
                type="number"
                value={newCoupon.per_user_limit}
                onChange={(e) => setNewCoupon({ ...newCoupon, per_user_limit: e.target.value })}
                inputProps={{ min: 0 }}
                error={!!errors.per_user_limit || isNaN(Number(newCoupon.per_user_limit))}
                helperText={errors.per_user_limit || (isNaN(Number(newCoupon.per_user_limit)) ? "Please enter a valid number" : "")}
                required
              />
              <FormControl>
                <InputLabel>Applicability</InputLabel>
                <Select
                  value={newCoupon.applicability}
                  onChange={(e) => setNewCoupon({ ...newCoupon, applicability: e.target.value })}
                >
                  <MenuItem value="all_plans">All Plans</MenuItem>
                  <MenuItem value="specific_plans">Specific Plans</MenuItem>
                </Select>
              </FormControl>
              {newCoupon.applicability === 'specific_plans' && (
                <TextField
                  label="Applicable Plan Names"
                  value={newCoupon.applicable_plan_names.join(', ')}
                  onChange={(e) => setNewCoupon({ 
                    ...newCoupon, 
                    applicable_plan_names: e.target.value.split(',').map(plan => plan.trim()) 
                  })}
                  helperText="Enter plan names separated by commas"
                />
              )}
              <TextField
                label="Applicable Features"
                value={newCoupon.applicable_features.join(', ')}
                onChange={(e) => setNewCoupon({ 
                  ...newCoupon, 
                  applicable_features: e.target.value.split(',').map(feature => feature.trim()) 
                })}
                helperText="Enter features separated by commas"
              />
              <FormControl>
                <InputLabel>Number Specific</InputLabel>
                <Select
                  value={newCoupon.is_number_specific}
                  onChange={(e) => setNewCoupon({ ...newCoupon, is_number_specific: e.target.value })}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
              {newCoupon.is_number_specific && (
                <TextField
                  label="Applicable Numbers"
                  value={newCoupon.applicable_numbers.join(', ')}
                  onChange={(e) => setNewCoupon({ 
                    ...newCoupon, 
                    applicable_numbers: e.target.value.split(',').map(num => num.trim()) 
                  })}
                  helperText="Enter phone numbers separated by commas"
                />
              )}
              <FormControl>
                <InputLabel>Open</InputLabel>
                <Select
                  value={newCoupon.open}
                  onChange={(e) => setNewCoupon({ ...newCoupon, open: e.target.value })}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Algo</InputLabel>
                <Select
                  value={newCoupon.algo}
                  onChange={(e) => setNewCoupon({ ...newCoupon, algo: e.target.value })}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={newCoupon.frequency}
                  onChange={(e) => setNewCoupon({ ...newCoupon, frequency: e.target.value })}
                >
                  <MenuItem value="one-time">One-time</MenuItem>
                  <MenuItem value="recurring">Recurring</MenuItem>
                </Select>
              </FormControl>
              {myRole === 'owner' ? (
                <TextField label="Owner" value={myOwner} InputProps={{ readOnly: true }} helperText="Credited to you" />
              ) : (
                <FormControl>
                  <InputLabel>Owner</InputLabel>
                  <Select
                    value={newCoupon.owner}
                    label="Owner"
                    onChange={(e) => setNewCoupon({ ...newCoupon, owner: e.target.value })}
                  >
                    <MenuItem value=""><em>None (Product)</em></MenuItem>
                    {ownersList.map((o) => (
                      <MenuItem key={`${o.owner}-${o.number}`} value={o.owner}>{o.owner}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDialog(false);
              setErrors({});
            }}>Cancel</Button>
            <Button 
              onClick={handleCreateCoupon} 
              variant="contained" 
              color="primary"
              disabled={
                isNaN(Number(newCoupon.value)) ||
                isNaN(Number(newCoupon.min_purchase_amount)) ||
                isNaN(Number(newCoupon.max_discount_amount)) ||
                isNaN(Number(newCoupon.usage_limit)) ||
                isNaN(Number(newCoupon.per_user_limit))
              }
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DashboardPage; 